// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import loadCatalogPageByPath from "@insite/client-framework/Store/Data/CatalogPages/Handlers/LoadCatalogPageByPath";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconPlus from "@turnerBlueprint/XDL/Icons/IconPlus";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Changes
    innerInformationContainer?: InjectableCss;
    subCategoryNameLinksListItem?: GridItemProps;
    subCategoryViewAllItem?: GridItemProps;
    subCategoryNameLinkInner?: LinkPresentationProps;
    viewAllLink?: LinkPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
    },
    subCategoryItem: {
        width: [12, 12, 4, 4, 2],
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [2, 2, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
            `,
        },
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    // XNG-Changes
    const { catalogPageState } = useAppSelector(state => {
        const location = getLocation(state);
        const categoryPath =
            getSelectedCategoryPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        return {
            catalogPageState: getCatalogPageStateByPath(state, categoryPath),
        };
    });
    const isBrandCategoryPage = catalogPageState.value?.breadCrumbs?.find(link => link.text === "Brands");
    const isCategoryLevelOne = isBrandCategoryPage
        ? (catalogPageState.value?.breadCrumbs || [])?.length <= 4
        : (catalogPageState.value?.breadCrumbs || [])?.length <= 2;

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem
                    key={subCategoryId}
                    {...styles.subCategoryItem}
                    // Xng-Changes
                    width={
                        !isCategoryLevelOne && BLUEPRINT_NAME === "turnerScienceBlueprint"
                            ? [12, 6, 2, 2, 2]
                            : [12, 4, 4, 4, 4]
                    }
                    {...{ isCategoryLevelOne }}
                >
                    <SubCategoryLink
                        categoryId={subCategoryId}
                        showImages={showImages}
                        // Xng-Changes
                        isCategoryLevelOne={isCategoryLevelOne}
                    />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    loadCatalogPageByPath,
    // Xng-Changes
    isCategoryLevelOne,
}: ReturnType<typeof mapStateToProps> & {
    showImages: boolean;
    // Xng-Changes
    isCategoryLevelOne: boolean;
} & ResolveThunks<typeof mapDispatchToProps>) => {
    /**
     * XNG-Changes:
     * Load subCategoryIds items
     */
    React.useEffect(() => {
        if (category && !category.subCategoryIds?.length) {
            loadCatalogPageByPath({ path: category.path });
        }
    }, []);

    if (!category) {
        return null;
    }

    // XNG-Changes
    return (
        <StyledWrapper {...styles.innerContainer}>
            {/* Xng-Changes */}
            <StyledWrapper {...styles.innerInformationContainer} {...{ isCategoryLevelOne }}>
                <StyledWrapper {...styles.subCategoryImageItem} {...{ isCategoryLevelOne }}>
                    {showImages && (
                        <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                            <LazyImage
                                src={category.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        </Clickable>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.subCategoryNameLinkItem}>
                    <Link
                        href={category.path}
                        {...styles.subCategoryNameLink}
                        data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                        // Xng-Changes
                        {...{ isCategoryLevelOne }}
                    >
                        {category.shortDescription}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.subCategoryNameLinksListItem}>
                {category.subCategoryIds?.slice(0, 4)?.map((subCategoryId: string) => (
                    <SubCategoryLinkInner categoryId={subCategoryId} key={subCategoryId} />
                ))}
            </StyledWrapper>
            {!!category.subCategoryIds && category.subCategoryIds.length > 4 && (
                <StyledWrapper {...styles.subCategoryViewAllItem}>
                    <Link
                        href={category.path}
                        {...styles.viewAllLink}
                        icon={{
                            iconProps: {
                                src: IconPlus,
                                size: 13,
                            },
                            position: "right",
                        }}
                        data-test-selector={`categoryDetailsSubCategoriesViewAll_${category.id}`}
                    >
                        {translate("View All")}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const mapStateToProps = (
    state: ApplicationState,
    ownProps: {
        categoryId: string;
    },
) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

// XNG-Changes
const mapDispatchToProps = {
    loadCatalogPageByPath,
};

// XNG-Changes
const SubCategoryLink = connect(mapStateToProps, mapDispatchToProps)(SubCategoryLinkView);

// XNG-Changes
const SubCategoryLinkInnerView = ({ category }: ReturnType<typeof linkInnerMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLinkInner}>
            {category.shortDescription}
        </Link>
    );
};

const linkInnerMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLinkInner = connect(linkInnerMapStateToProps)(SubCategoryLinkInnerView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
